/**
 * Mock fixtures for the Skills feature (frontend-only).
 *
 * A Skill is a reusable, NON-EDITABLE research/automation recipe. Users create
 * them or clone public ones into their personal library, then "Run now" to send
 * the skill straight to the agent in a fresh chat.
 *
 * Each skill has TWO bodies:
 *   - `explanation_md` — the friendly "what this skill does" writeup a human reads.
 *   - `raw_md` — the literal instruction text we send the agent when the skill runs.
 * The detail page toggles between them.
 *
 * Data model mirrors the planned backend doc (`chat_history.alerts` with
 * `kind:"skill"`):
 *   - Skills are CREATE + DELETE only, never edited. A personal copy is a frozen
 *     clone of a public skill, so the two can never drift.
 *   - `data_sources` reference IDs from the shared data catalog
 *     (`lib/data-catalog.ts`) so the chips match the "Data +" picker.
 *
 * Copy rule: NO em dashes anywhere (see .cursor/rules/xynth-frontend-copy-and-styling.mdc).
 *
 * Nothing here talks to the backend. When the backend lands, replace
 * `getMockSkill` / `MOCK_SKILLS` with a real fetch; props are 1:1 with these types.
 */

/** What the skill ultimately produces when the agent runs it. */
export type SkillOutputType =
  | "research"
  | "alert"
  | "signal"
  | "dashboard"
  | "screener";

/** A data source the skill uses. `id` matches `lib/data-catalog.ts` where possible. */
export interface SkillDataSource {
  id: string;
  name: string;
  /** Catalog category, e.g. "Options", "Screeners", "Fundamentals", "Stocks". */
  category: string;
}

export interface Skill {
  id: string;
  /** Short, human name. */
  name: string;
  /** One-line blurb for the grid card. */
  description: string;
  /** Friendly "what this skill does" body, rendered as markdown. */
  explanation_md: string;
  /** The literal instruction text sent to the agent when the skill runs. */
  raw_md: string;
  /** "Xynth" for first-party, "@handle" for community. */
  author: string;
  /** Public = lives in the library for anyone to clone. Personal = a user's own. */
  is_public: boolean;
  /** What running the skill creates. */
  output_type: SkillOutputType;
  /** Data sources the skill touches (chips). */
  data_sources: SkillDataSource[];
  /** ISO timestamp. */
  created_at: string;
  /** How many times the skill has been run. */
  usage_count: number;
  /**
   * UI-only flag for the mock: whether the viewing user already has this in
   * their personal library. Drives Add ↔ Remove button state.
   */
  added_to_personal?: boolean;
  /**
   * Personal library action. Cloned public skills are "unadd"; user-authored
   * personal skills are "delete".
   */
  personal_action?: "unadd" | "delete";
  /** Public source id when this row is a personal clone of a public skill. */
  cloned_from?: string | null;
  /** The single seeded "Create a Skill" skill, present for everyone, never deletable. */
  undeletable?: boolean;
}

const CREATE_A_SKILL_EXPLANATION = `# Create a Skill

This is your guide to building the best possible skill. Launch it and Xynth will
interview you, then draft a clean, reusable skill with you.

## What makes a great skill
- **One clear job.** A skill should do exactly one thing well, like "deep dive a
  ticker", not "do everything".
- **Swappable inputs.** Write it so the ticker, date, or watchlist can change on
  every run.
- **Names its data.** Say which data sources the agent should reach for, so runs
  are fast and consistent.
- **Ends with a clear output.** Research answer, alert, signal, dashboard, or
  screener. Pick one.

## How it works
1. Tell Xynth what you want the skill to do.
2. Answer a few quick questions about inputs, data, and output type.
3. Xynth drafts the skill in markdown. You approve or tweak it.
4. It saves to your personal skills and shows up in your sidebar.

> Skills cannot be edited after they are made, only created and deleted. If you
> want a change, just create a fresh one. Clean and simple.`;

const CREATE_A_SKILL_RAW = `You are helping the user create a new reusable Xynth skill. Interview them, then
draft the skill.

Ask, one at a time:
1. What should this skill do, in one sentence?
2. What inputs change each run (ticker, date, watchlist)?
3. Which data sources should it use?
4. What is the output type (research, alert, signal, dashboard, screener)?

Then write the skill as clean markdown with these sections: title, "What it does",
"Inputs", "Output". Keep it to one clear job. Use swappable inputs. Name the data
sources explicitly. Do not use em dashes. Show the draft and let the user approve
or tweak before saving.`;

const TICKER_DEEP_DIVE_EXPLANATION = `# Ticker Deep Dive

A complete, repeatable work-up on a single ticker. Swap the ticker each run.

## What it does
Pulls the full picture on one name and hands you a clear read:
- **Price and trend.** Recent action versus the 20, 50, and 200 day.
- **Options posture.** Flow, gamma, and where the big premium is leaning.
- **Dark pool.** Notable off-exchange prints and net positioning.
- **Fundamentals.** The numbers that actually move the stock.
- **Catalysts.** News and what the market is reacting to.

## Inputs
- \`ticker\`: the symbol to analyze.

## How to use
Click Run now, then tell Xynth which ticker you want. For example: "Deep dive TSLA."
You can swap in any symbol each time you run it.

## Output
A research brief: the setup in one line, the evidence below it, and the risk.`;

const TICKER_DEEP_DIVE_RAW = `Do a complete deep dive on {ticker}. Use batch() to pull, in parallel:
stock_price_bars (compare to 20/50/200 day), recent_flows and options gamma,
dark pool prints, and company_snapshot fundamentals. Also search recent news for
catalysts.

Then write a research brief: open with the setup in one line, support it with the
evidence from each data source, and end with the key risk. Be concrete and use
real numbers. Do not use em dashes.`;

const EARNINGS_PREVIEW_EXPLANATION = `# Earnings Preview

Everything you need before a name reports. Swap the ticker each run.

## What it does
- **Implied move.** What options are pricing for the print.
- **IV rank and term structure.** Is volatility rich or cheap right now.
- **History.** How the stock has reacted to its last several reports.
- **Positioning.** Where the flow is leaning into the event.

## Inputs
- \`ticker\`: the symbol reporting.

## How to use
Click Run now and tell Xynth which name is reporting. For example: "Run Earnings
Preview on NVDA." Optionally add your position size or portfolio value and Xynth
will size the takeaways to you.

## Output
A pre-earnings brief with the implied move, the vol setup, and the historical
reaction pattern.`;

const EARNINGS_PREVIEW_RAW = `Build a pre-earnings preview for {ticker}. Use batch() to pull: implied_moves,
iv_rank, iv_term_structure, and earnings_history. Add recent options flow to read
positioning into the event.

Write a brief covering the implied move, whether IV is rich or cheap, and how the
stock has reacted to its last several reports. End with the cleanest way to
express a view. Do not use em dashes.`;

const WHEEL_FINDER_EXPLANATION = `# Wheel / CSP Finder

Scan for the cleanest cash secured put setups for the week.

## What it does
Screens liquid names for rich premium, healthy IV rank, and supportive levels,
then ranks the best wheel candidates.

## Inputs
- \`watchlist\`: optional list of tickers. Defaults to liquid large caps.
- \`min_premium\`: minimum annualized yield to consider.

## How to use
Click Run now. Give Xynth a watchlist or use the default large caps, and your
minimum yield. Optionally add your available cash and Xynth will size each put to
what you can actually secure.

## Output
A ranked screener of cash secured put candidates with strike, premium, and yield.`;

const WHEEL_FINDER_RAW = `Find the best cash secured put setups this week. Screen {watchlist} (default:
liquid large caps) for healthy IV rank and supportive price levels, then use the
options_chain to find puts with strong annualized premium above {min_premium}.

Rank the candidates and return a table with ticker, strike, expiry, premium, and
annualized yield. Do not use em dashes.`;

export const MOCK_SKILLS: Skill[] = [
  {
    id: "create-a-skill",
    name: "Create a Skill",
    description: "Build your own reusable skill. Xynth guides you through it.",
    explanation_md: CREATE_A_SKILL_EXPLANATION,
    raw_md: CREATE_A_SKILL_RAW,
    author: "Xynth",
    is_public: true,
    output_type: "research",
    data_sources: [],
    created_at: "2026-06-01T00:00:00Z",
    usage_count: 18204,
    added_to_personal: true,
    undeletable: true,
  },
  {
    id: "ticker-deep-dive",
    name: "Ticker Deep Dive",
    description: "Full work-up on one ticker: price, flow, dark pool, fundamentals, news.",
    explanation_md: TICKER_DEEP_DIVE_EXPLANATION,
    raw_md: TICKER_DEEP_DIVE_RAW,
    author: "Xynth",
    is_public: true,
    output_type: "research",
    data_sources: [
      { id: "stock_price_bars", name: "Price Bars", category: "Stocks" },
      { id: "recent_flows", name: "Options Flow", category: "Options" },
      { id: "options_gex", name: "Gamma Exposure", category: "Options" },
      { id: "darkpool_prints", name: "Dark Pool", category: "Options" },
      { id: "company_snapshot", name: "Fundamentals", category: "Fundamentals" },
    ],
    created_at: "2026-06-12T00:00:00Z",
    usage_count: 1204,
    added_to_personal: false,
  },
  {
    id: "earnings-preview",
    name: "Earnings Preview",
    description: "IV, implied move, history, and positioning into a name's earnings.",
    explanation_md: EARNINGS_PREVIEW_EXPLANATION,
    raw_md: EARNINGS_PREVIEW_RAW,
    author: "Xynth",
    is_public: true,
    output_type: "research",
    data_sources: [
      { id: "implied_moves", name: "Implied Move", category: "Options" },
      { id: "iv_rank", name: "IV Rank", category: "Options" },
      { id: "iv_term_structure", name: "Term Structure", category: "Options" },
      { id: "earnings_history", name: "Earnings History", category: "Fundamentals" },
    ],
    created_at: "2026-06-13T00:00:00Z",
    usage_count: 642,
    added_to_personal: false,
  },
  {
    id: "wheel-finder",
    name: "Wheel / CSP Finder",
    description: "Scan for the cleanest cash secured put setups this week.",
    explanation_md: WHEEL_FINDER_EXPLANATION,
    raw_md: WHEEL_FINDER_RAW,
    author: "@optionsmike",
    is_public: true,
    output_type: "screener",
    data_sources: [
      { id: "options_chain", name: "Options Chain", category: "Options" },
      { id: "iv_rank", name: "IV Rank", category: "Options" },
      { id: "stock_quotes", name: "Quotes", category: "Stocks" },
    ],
    created_at: "2026-06-15T00:00:00Z",
    usage_count: 318,
    added_to_personal: false,
  },
];

/** Look up one skill by id. Falls back to the first skill if not found. */
export function getMockSkill(id: string | undefined): Skill {
  if (!id) return MOCK_SKILLS[0];
  return MOCK_SKILLS.find((s) => s.id === id) ?? MOCK_SKILLS[0];
}
